/**
 * useLeadCapture — real-time progressive lead capture.
 *
 * Wire into every public form (shop checkout, quiz, IV booking, contact,
 * signup, public intake). Call `capture({ first_name: "Steve" })` on every
 * onChange — debounced 400ms, NOT on blur/Enter. Partial values are sent
 * so a half-typed first name still gets stored under one evolving record
 * per visitor.
 *
 *   const { capture, visitorId } = useLeadCapture("shop");
 *   <input
 *      onChange={(e) => {
 *        setName(e.target.value);
 *        capture({ first_name: e.target.value });
 *      }}
 *   />
 *
 * The hook merges fields on the front-end and posts the FULL accumulated
 * form state on each debounce tick so the back-end upsert sees consistent
 * data. The back-end de-dupes the visitor row by visitor_id and runs an
 * auto-link pass against applicants.email / phone every write.
 *
 * Identity/contact fields only — never quiz answers or medical fields
 * (see /app/backend/routes/lead_capture.py _ALLOWED_FIELDS).
 */
import { useCallback, useEffect, useMemo, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_KEY = "pr_visitor_id";

function ensureVisitorId() {
  if (typeof window === "undefined") return "";
  let id = window.localStorage.getItem(LS_KEY);
  if (!id) {
    id = (window.crypto?.randomUUID?.() || `v_${Date.now()}_${Math.random().toString(36).slice(2)}`);
    window.localStorage.setItem(LS_KEY, id);
  }
  return id;
}

export default function useLeadCapture(source = "unknown", { debounceMs = 400 } = {}) {
  // Stable visitor id per browser; survives navigation/reloads.
  const visitorId = useMemo(ensureVisitorId, []);
  const pendingRef = useRef({});      // accumulated unsent fields
  const sentRef = useRef({});         // last sent snapshot — keeps from refiring identical posts
  const timerRef = useRef(null);
  const lastSourceRef = useRef(source);

  // If the consumer remounts and changes `source`, keep the latest.
  useEffect(() => { lastSourceRef.current = source; }, [source]);

  const flush = useCallback(async () => {
    timerRef.current = null;
    const fields = { ...pendingRef.current };
    pendingRef.current = {};
    // Drop fields that haven't changed since the last send.
    const changed = {};
    let hasChange = false;
    for (const [k, v] of Object.entries(fields)) {
      if (sentRef.current[k] !== v) { changed[k] = v; hasChange = true; }
    }
    if (!hasChange) return;

    try {
      await axios.post(
        `${API}/public/leads/capture`,
        { visitor_id: visitorId, source: lastSourceRef.current, fields: changed },
        { timeout: 6000, withCredentials: false },
      );
      sentRef.current = { ...sentRef.current, ...changed };
    } catch {
      // Network blip: re-merge so the next tick retries. We don't surface
      // anything to the UI — lead capture is best-effort.
      pendingRef.current = { ...changed, ...pendingRef.current };
    }
  }, [visitorId]);

  const capture = useCallback((patch) => {
    if (!patch || typeof patch !== "object") return;
    pendingRef.current = { ...pendingRef.current, ...patch };
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(flush, debounceMs);
  }, [flush, debounceMs]);

  // Force-flush on unmount so the last keystroke before navigation is saved.
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        // fire-and-forget — no await
        flush();
      }
    };
  }, [flush]);

  return { capture, visitorId, flush };
}
